import { shared } from '../three/shared';
import { ZONES } from '../config/zones';

// Procedural, binaural (HRTF) soundscape for The Jungle of Bias.
// All sound is synthesized at runtime — no audio asset files required.
class SoundEngine {
  constructor() {
    this.ctx = null;
    this.started = false;
    this.enabled = true;
    this.emitters = [];
    this.master = null;
    this.globals = {};
    this.narr = { source: null, panner: null, attached: false };
    this.narrationVolume = 1;
    this._acc = 0;
    this._howlerTimer = null;
  }

  async start() {
    if (this.started) {
      if (this.ctx && this.ctx.state === 'suspended') { try { await this.ctx.resume(); } catch (e) {} }
      return;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      await this.ctx.resume();

      this.master = this.ctx.createGain();
      this.master.gain.value = this.enabled ? 0.85 : 0.0001;
      this.master.connect(this.ctx.destination);

      this._buffers = {
        metallic: this._makeBuffer(4, (d, sr) => this._fillMetallic(d, sr)),
        choral: this._makeBuffer(6, (d, sr) => this._fillChoral(d, sr)),
        water: this._makeBuffer(5, (d, sr) => this._fillWater(d, sr)),
      };

      this._buildEmitters();
      this._buildGlobals();
      this._startHowlers();

      this.started = true;
    } catch (e) {
      console.warn('SoundEngine start failed', e);
    }
  }

  setEnabled(on) {
    this.enabled = on;
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    // Master bus — near-instant so muting is immediate.
    if (this.master) this.master.gain.setTargetAtTime(on ? 0.85 : 0.0001, now, 0.02);
    // When muting, hard-clamp every bus (narration/briefings, Outrage-Storm wind,
    // night drone, and all positional emitters) so nothing can linger.
    if (!on) {
      if (this.narr && this.narr.gain) this.narr.gain.gain.setTargetAtTime(0.0001, now, 0.02);
      if (this.globals.windGain) this.globals.windGain.gain.setTargetAtTime(0.0001, now, 0.02);
      if (this.globals.droneGain) this.globals.droneGain.gain.setTargetAtTime(0.0001, now, 0.02);
      for (const e of this.emitters) e.gain.gain.setTargetAtTime(0.0001, now, 0.02);
    }
  }

  setNarrationBank(bank) {
    this.narr.bank = bank; // 'progressive' | 'traditional' | null
  }

  // Harpy Eagle guide volume (0..1): scales the narration path while ambience stays.
  setNarrationVolume(v) {
    this.narrationVolume = Math.max(0, Math.min(1, v));
    if (this.narr && this.narr.gain && this.ctx) {
      this.narr.gain.gain.setTargetAtTime(this.enabled ? 1.4 * this.narrationVolume : 0.0001, this.ctx.currentTime, 0.08);
    }
  }

  // ---- Buffer synthesis ----
  _makeBuffer(seconds, fill) {
    const sr = this.ctx.sampleRate;
    const len = Math.floor(sr * seconds);
    const buf = this.ctx.createBuffer(2, len, sr);
    for (let ch = 0; ch < 2; ch++) fill(buf.getChannelData(ch), sr);
    return buf;
  }

  _fillMetallic(d, sr) {
    // High-frequency data tickers: sparse decaying metallic pings + shimmer.
    for (let i = 0; i < d.length; i++) d[i] = 0;
    const n = Math.floor(d.length / (sr * 0.09)); // ~11 pings/sec
    for (let k = 0; k < n; k++) {
      const start = Math.floor(Math.random() * d.length);
      const freq = 1800 + Math.random() * 3200;
      const dur = Math.floor(sr * (0.02 + Math.random() * 0.05));
      for (let j = 0; j < dur && start + j < d.length; j++) {
        const env = Math.exp(-j / (dur * 0.4));
        d[start + j] += Math.sin((2 * Math.PI * freq * j) / sr) * env * 0.18;
      }
    }
    // faint high shimmer
    for (let i = 0; i < d.length; i++) {
      d[i] += Math.sin((2 * Math.PI * 5200 * i) / sr) * 0.015 * (0.5 + 0.5 * Math.sin(i * 0.0005));
    }
  }

  _fillChoral(d, sr) {
    // Deep resonant choral pad + slow stone-grind noise.
    const roots = [110, 146.83, 220, 277.18]; // A2, D3, A3, C#4 — solemn chord
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const t = i / sr;
      let v = 0;
      for (const f of roots) {
        v += Math.sin(2 * Math.PI * f * t + Math.sin(t * 0.3) * 0.5) * 0.06;
      }
      // slow amplitude swell
      v *= 0.6 + 0.4 * Math.sin(t * 0.25);
      // stone grind = lowpassed noise
      const noise = (Math.random() * 2 - 1);
      last = last * 0.96 + noise * 0.04;
      v += last * 0.05 * (0.5 + 0.5 * Math.sin(t * 0.7));
      d[i] = v;
    }
  }

  _fillWater(d, sr) {
    // Turbulent river: brown noise + periodic piranha splash bursts.
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      d[i] = last * 2.2 * 0.22;
    }
    const splashes = 6;
    for (let s = 0; s < splashes; s++) {
      const start = Math.floor(Math.random() * d.length);
      const dur = Math.floor(sr * (0.08 + Math.random() * 0.12));
      for (let j = 0; j < dur && start + j < d.length; j++) {
        const env = Math.exp(-j / (dur * 0.3));
        d[start + j] += (Math.random() * 2 - 1) * env * 0.28;
      }
    }
  }

  _panner(x, y, z) {
    const p = this.ctx.createPanner();
    p.panningModel = 'HRTF';
    p.distanceModel = 'inverse';
    p.refDistance = 18;
    p.maxDistance = 260;
    p.rolloffFactor = 1.1;
    if (p.positionX) { p.positionX.value = x; p.positionY.value = y; p.positionZ.value = z; }
    else p.setPosition(x, y, z);
    return p;
  }

  _loopSource(buffer) {
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.playbackRate.value = 0.96 + Math.random() * 0.08;
    return src;
  }

  _buildEmitters() {
    const add = (x, y, z, kind, base) => {
      const src = this._loopSource(this._buffers[kind]);
      const gain = this.ctx.createGain();
      gain.gain.value = 0.0001;
      const panner = this._panner(x, y, z);
      src.connect(gain).connect(panner).connect(this.master);
      try { src.start(this.ctx.currentTime + Math.random() * 0.5); } catch (e) {}
      this.emitters.push({ x, y, z, kind, base, gain });
    };
    ZONES.forEach((z, i) => {
      add(z.x, 7, -60, 'metallic', 0.5); // Left · Progressive tickers
      add(z.x, 7, 60, 'choral', 0.55); // Right · Traditional choir/stone
      if (i % 3 === 1) add(z.x, 1, 0, 'water', 0.7); // River turbulence
    });
  }

  _buildGlobals() {
    // Night "quiet echo chamber" drone
    const droneOsc = this.ctx.createOscillator();
    droneOsc.type = 'sine';
    droneOsc.frequency.value = 55;
    const droneOsc2 = this.ctx.createOscillator();
    droneOsc2.type = 'sine';
    droneOsc2.frequency.value = 82.4;
    const droneGain = this.ctx.createGain();
    droneGain.gain.value = 0.0001;
    droneOsc.connect(droneGain);
    droneOsc2.connect(droneGain);
    droneGain.connect(this.master);
    try { droneOsc.start(); droneOsc2.start(); } catch (e) {}

    // Outrage-storm wind = filtered noise
    const noise = this._loopSource(this._makeBuffer(3, (d) => { for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1; }));
    const windFilter = this.ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 500;
    windFilter.Q.value = 0.6;
    const windGain = this.ctx.createGain();
    windGain.gain.value = 0.0001;
    noise.connect(windFilter).connect(windGain).connect(this.master);
    try { noise.start(); } catch (e) {}

    this.globals = { droneGain, windGain, windFilter };
  }

  _startHowlers() {
    const schedule = () => {
      this._howlerTimer = setTimeout(() => {
        this._howl();
        schedule();
      }, 3500 + Math.random() * 5000);
    };
    schedule();
  }

  _howl() {
    if (!this.ctx || !this.enabled) return;
    const day = 1 - shared.env.night;
    if (day < 0.45) return; // howler monkeys vocalize by day
    try {
      const t = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.14, t + 0.15);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      osc.frequency.setValueAtTime(320, t);
      osc.frequency.exponentialRampToValueAtTime(140, t + 0.5);
      osc.frequency.exponentialRampToValueAtTime(240, t + 1.0);
      const lp = this.ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 1200;
      const side = Math.random() > 0.5 ? 1 : -1;
      const cam = shared.camPos;
      const p = this._panner(cam.x + (Math.random() - 0.5) * 40, 14, side * (40 + Math.random() * 40));
      osc.connect(lp).connect(g).connect(p).connect(this.master);
      osc.start(t);
      osc.stop(t + 1.2);
    } catch (e) {}
  }

  // ---- Spatial narration (routes the TTS <audio> element through an HRTF panner at the eagle) ----
  attachNarration(el) {
    if (!this.started || !this.ctx || this.narr.attached) return false;
    try {
      const source = this.ctx.createMediaElementSource(el);
      const panner = this._panner(shared.eaglePos.x, shared.eaglePos.y, shared.eaglePos.z);
      panner.refDistance = 30;
      panner.rolloffFactor = 0.6;
      const g = this.ctx.createGain();
      g.gain.value = this.enabled ? 1.4 * this.narrationVolume : 0.0001;
      source.connect(g).connect(panner).connect(this.master);
      this.narr = { source, panner, gain: g, attached: true };
      return true;
    } catch (e) {
      console.warn('attachNarration failed', e);
      return false;
    }
  }

  // ---- Spatial one-shot: whale breach 'thud' + splash ----
  playThud(x, y, z) {
    if (!this.started || !this.ctx || !this.enabled) return;
    try {
      const t = this.ctx.currentTime;
      const p = this._panner(x, y, z);
      p.connect(this.master);
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(120, t);
      osc.frequency.exponentialRampToValueAtTime(38, t + 0.5);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.55, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.95);
      osc.connect(g).connect(p);
      osc.start(t); osc.stop(t + 1.0);
      // splash = short filtered noise burst
      const noise = this.ctx.createBufferSource();
      noise.buffer = this._makeBuffer(0.4, (d) => { for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (d.length * 0.25)); });
      const lp = this.ctx.createBiquadFilter();
      lp.type = 'lowpass'; lp.frequency.value = 900;
      const ng = this.ctx.createGain();
      ng.gain.value = 0.35;
      noise.connect(lp).connect(ng).connect(p);
      try { noise.start(t); } catch (e) {}
    } catch (e) {}
  }

  // ---- Per-frame update (throttled) ----
  update(delta) {
    if (!this.started || !this.ctx) return;
    this._acc += delta;
    if (this._acc < 0.08) return;
    const dt = this._acc;
    this._acc = 0;

    const ctx = this.ctx;
    const now = ctx.currentTime;
    const cam = shared.camPos;
    const dir = shared.camDir;
    const L = ctx.listener;

    // Binaural listener follows the camera
    if (L.positionX) {
      L.positionX.setTargetAtTime(cam.x, now, 0.05);
      L.positionY.setTargetAtTime(cam.y, now, 0.05);
      L.positionZ.setTargetAtTime(cam.z, now, 0.05);
      L.forwardX.setTargetAtTime(dir.x, now, 0.05);
      L.forwardY.setTargetAtTime(dir.y, now, 0.05);
      L.forwardZ.setTargetAtTime(dir.z, now, 0.05);
      L.upX.value = 0; L.upY.value = 1; L.upZ.value = 0;
    } else if (L.setPosition) {
      L.setPosition(cam.x, cam.y, cam.z);
      L.setOrientation(dir.x, dir.y, dir.z, 0, 1, 0);
    }

    const night = shared.env.night;
    const storm = shared.env.storm;

    // Distance-gated positional emitters — silenced entirely while muted so no
    // proximity/animal loop can raise volume when audioEnabled is false.
    for (const e of this.emitters) {
      const dx = e.x - cam.x, dy = e.y - cam.y, dz = e.z - cam.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      let audible = Math.max(0, 1 - dist / 150);
      audible *= audible;
      let g = e.base * audible;
      if (e.kind === 'metallic') g *= 0.6 + 0.6 * (1 - night); // brighter tickers by day
      if (e.kind === 'choral') g *= 0.7 + 0.5 * night; // deeper resonance at night
      g *= 1 - 0.4 * storm;
      e.gain.gain.setTargetAtTime(this.enabled ? Math.max(0.0001, g * 0.5) : 0.0001, now, 0.25);
    }

    // Global layers — weather/drone can never come back up while muted.
    if (this.globals.droneGain) this.globals.droneGain.gain.setTargetAtTime(this.enabled ? 0.0001 + night * 0.22 : 0.0001, now, 0.4);
    if (this.globals.windGain) {
      this.globals.windGain.gain.setTargetAtTime(this.enabled ? 0.0001 + storm * 0.5 : 0.0001, now, 0.3);
      this.globals.windFilter.frequency.setTargetAtTime(400 + storm * 900, now, 0.3);
    }

    // Move narration source to the eagle, biased toward the bank being discussed (per-ear)
    if (this.narr.attached && this.narr.panner) {
      const ep = shared.eaglePos;
      const off = this.narr.bank === 'progressive' ? -16 : this.narr.bank === 'traditional' ? 16 : 0;
      const p = this.narr.panner;
      if (p.positionX) {
        p.positionX.setTargetAtTime(ep.x, now, 0.08);
        p.positionY.setTargetAtTime(ep.y, now, 0.08);
        p.positionZ.setTargetAtTime(ep.z + off, now, 0.08);
      } else p.setPosition(ep.x, ep.y, ep.z + off);
    }
  }
}

export const soundEngine = new SoundEngine();
